import React, { useState, useEffect } from 'react';
import { X, BookOpen, Clock, FileText, Package, ChevronRight, ChevronLeft, Check, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import formationService from '../../services/formationService';
import { apiClient } from '../../services/authService';
import MultiSelectDropdown from '../MultiSelectDropdown';
import './AddFormationDialog.css';

const AddFormationDialog = ({ isOpen, onClose, onFormationAdded }) => {
  const { t } = useTranslation();
  const { isResponsable, isAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_days: 1,
    is_active: true,
    centre: ''
  });
  const [availableConsommables, setAvailableConsommables] = useState([]);
  const [selectedConsommables, setSelectedConsommables] = useState([]);
  const [availableCentres, setAvailableCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && step === 1) {
      if (isAdmin) {
        fetchAvailableCentres();
      }
    }
    if (isOpen && step === 2) {
      fetchAvailableConsommables();
    }
  }, [isOpen, step]);

  const fetchAvailableCentres = async () => {
    try {
      const response = await apiClient.get('/centres/');
      setAvailableCentres(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching centres:', err);
      setAvailableCentres([]);
    }
  };

  const fetchAvailableConsommables = async () => {
    try {
      const response = await apiClient.get('/consommables/');
      setAvailableConsommables(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching consommables:', err);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNext = () => {
    if (!formData.title.trim()) {
      setError('Le titre est obligatoire');
      return;
    }
    if (isAdmin && !formData.centre) {
      setError('Le centre est obligatoire');
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSkip = async () => {
    await submitFormation([]);
  };

  const handleSubmit = async () => {
    await submitFormation(selectedConsommables);
  };

  const submitFormation = async (consommablesList) => {
    setLoading(true);
    setError(null);

    try {
      // Préparer les consommables avec leur ID pour lier les existants
      const consommablesPayload = consommablesList.map(item => {
        const originalConsommable = availableConsommables.find(c => c.id === item.consommable_id);
        if (!originalConsommable) return null;
        
        // Envoyer l'ID pour lier le consommable existant
        return {
          id: originalConsommable.id,
          name: originalConsommable.name,
          unit_price: originalConsommable.unit_price,
          type_consommable: originalConsommable.type_consommable,
          is_per_day: !!originalConsommable.is_per_day,
          supplier: originalConsommable.supplier?.id || originalConsommable.supplier,
          quantity_fixe: originalConsommable.quantity_fixe,
          quantity_per_student: originalConsommable.quantity_per_student,
          quantity_per_trainer: originalConsommable.quantity_per_trainer,
          quantity_per_visitor: originalConsommable.quantity_per_visitor,
          quantity_per_staff: originalConsommable.quantity_per_staff,
        };
      }).filter(Boolean);

      const response = await formationService.create({
        ...formData,
        duration_days: parseInt(formData.duration_days, 10),
        trainers: formData.trainers,
        ...(isAdmin ? { centre: formData.centre } : {}),
        consommables: consommablesPayload
      });
      
      onFormationAdded(response);
      resetForm();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création de la formation');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      title: '',
      description: '',
      duration_days: 1,
      is_active: true,
      trainers: [],
      centre: ''
    });
    setSelectedConsommables([]);
    setError(null);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const addConsommable = () => {
    setSelectedConsommables(prev => [...prev, {
      id: Date.now(),
      consommable_id: '',
      quantity: 1
    }]);
  };

  const updateConsommable = (index, field, value) => {
    setSelectedConsommables(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const removeConsommable = (index) => {
    setSelectedConsommables(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="dialog-overlay ui-dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container wizard-container ui-dialog-container">
        <div className="dialog-header ui-dialog-header">
          <div className="dialog-title ui-dialog-title">
            <BookOpen size={24} />
            <h2>Nouvelle Formation</h2>
          </div>
          <button className="dialog-close" onClick={onClose} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        <div className="wizard-steps">
          <div className={`step-indicator ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <span>Informations</span>
          </div>
          <div className="step-line"></div>
          <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <span>Consommables</span>
          </div>
        </div>

        {error && (
          <div className="dialog-error">
            {error}
          </div>
        )}

        {step === 1 && (
          <form className="dialog-form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
            <div className="form-group">
              <label htmlFor="title" className="ui-label">
                <BookOpen size={16} />
                Titre *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Nom de la formation"
                required
                className="ui-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description" className="ui-label">
                <FileText size={16} />
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Description de la formation..."
                rows={3}
                className="ui-textarea"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration_days" className="ui-label">
                  <Clock size={16} />
                  Durée (jours) *
                </label>
                <input
                  type="number"
                  id="duration_days"
                  name="duration_days"
                  value={formData.duration_days}
                  onChange={handleChange}
                  min="1"
                  max="365"
                  required
                  className="ui-input"
                />
              </div>

              {isAdmin && (
                <div className="form-group">
                  <label htmlFor="centre" className="ui-label">
                    Centre *
                  </label>
                  <select
                    id="centre"
                    name="centre"
                    value={formData.centre}
                    onChange={handleChange}
                    required
                    className="ui-input ui-select"
                  >
                    <option value="">Sélectionner un centre</option>
                    {availableCentres.map((c) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <span>Formation active</span>
                </label>
              </div>
            </div>

            <div className="dialog-actions ui-dialog-actions">
              <button
                type="button"
                className="btn-secondary ui-btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-primary ui-btn-primary"
              >
                Suivant
                <ChevronRight size={18} />
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="dialog-form">
            <div className="consommables-section">
              <div className="section-header">
                <h3>
                  <Package size={20} />
                  Consommables associés
                </h3>
                <span className="section-hint">Optionnel - vous pouvez ajouter plus tard</span>
              </div>

              {selectedConsommables.length === 0 ? (
                <div className="empty-consommables">
                  <Package size={48} />
                  <p>Aucun consommable sélectionné</p>
                  <button
                    type="button"
                    className="btn-add-item"
                    onClick={addConsommable}
                  >
                    <Plus size={16} />
                    Ajouter un consommable
                  </button>
                </div>
              ) : (
                <div className="consommables-list">
                  {selectedConsommables.map((item, index) => (
                    <div key={item.id} className="consommable-row">
                      <select
                        value={item.consommable_id}
                        onChange={(e) => updateConsommable(index, 'consommable_id', e.target.value)}
                        className="consommable-select"
                      >
                        <option value="">Sélectionner un consommable...</option>
                        {availableConsommables.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.unit_price} €)
                          </option>
                        ))}
                      </select>
                      <div className="consommable-info">
                        <span className="consommable-type-tag">
                          {t(`consommables.types.${availableConsommables.find(c => c.id === item.consommable_id)?.type_consommable || 'FIXE'}`)}
                        </span>
                      </div>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateConsommable(index, 'quantity', parseInt(e.target.value) || 0)}
                        min="0"
                        className="quantity-input"
                        placeholder="Qté"
                      />
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeConsommable(index)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn-add-item"
                    onClick={addConsommable}
                  >
                    <Plus size={16} />
                    Ajouter un autre consommable
                  </button>
                </div>
              )}
            </div>

            <div className="dialog-actions ui-dialog-actions">
              <button
                type="button"
                className="btn-secondary ui-btn-secondary"
                onClick={handleBack}
                disabled={loading}
              >
                <ChevronLeft size={18} />
                Retour
              </button>
              <div className="actions-right">
                <button
                  type="button"
                  className="btn-skip"
                  onClick={handleSkip}
                  disabled={loading}
                >
                  Ignorer cette étape
                </button>
                <button
                  type="button"
                  className="btn-primary ui-btn-primary"
                  onClick={handleSubmit}
                  disabled={loading || selectedConsommables.some(c => !c.consommable_id)}
                >
                  {loading ? 'Création...' : (
                    <>
                      <Check size={18} />
                      Créer la formation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddFormationDialog;
